import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { planGetPlanHistory } from 'PlanPackage/MainModule/actions/planActions';
import {
  getKnmPlanApprovedHistoryChanges,
  getPlanIsLoadingHistory,
} from 'PlanPackage/MainModule/selectors/planSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';
import { mapErknmPlanStatusByStatusTitle } from 'APIPackage/PlanListModule/constants/mapErknmPlanStatusByStatusTitle';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ErknmPlanStatusTitlesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusTitlesEnum';
import { IPlanHistoryItem } from 'PlanPackage/MainModule/interfaces/IPlanHistoryItem';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getKnmDataPlanId } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KNM_PLAN_HISTORY } from 'CorePackage/MainModule/routes/routes';
import { getPlanListPlan } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';

import styles from './ErknmPlanHistory.module.scss';

const PAGE_SIZE = 10;

interface IErknmPlanHistoryProps {
  planId: number | null | undefined;
  isLoading: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  fullNameWhoConsideredPlan: string | null | undefined;
  positionWhoConsideredPlan: string | null | undefined;
  approvedHistoryChanges: IPlanHistoryItem[];
}

const ErknmPlanHistoryWithoutStore = ({
  planId,
  isLoading,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  fullNameWhoConsideredPlan,
  positionWhoConsideredPlan,
  approvedHistoryChanges,
}: IErknmPlanHistoryProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [didVisible, setDidVisible] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const [itemsPage, setItemsPage] = useState<number>(0);
  const lastVisibleItemsNumber = (itemsPage + 1) * PAGE_SIZE;

  const getHistory = useCallback(() => planId && dispatch(planGetPlanHistory(planId.toString())), [dispatch, planId]);

  useEffect(() => {
    if (planId) {
      getHistory();
    }
  }, [dispatch, getHistory, planId]);

  const updateDidVisible = useCallback(() => {
    const blockOffsetTop = blockRef.current?.getBoundingClientRect().top;

    if (blockOffsetTop && blockOffsetTop - document.documentElement.clientHeight < 0) {
      setDidVisible(true);
    }
  }, []);

  useEffect(() => {
    updateDidVisible();
    window.addEventListener('scroll', updateDidVisible);

    return () => window.removeEventListener('scroll', updateDidVisible);
  }, [updateDidVisible]);

  useEffect(() => {
    if (didVisible && planId) {
      dispatch(planGetPlanHistory(planId.toString()));
    }
  }, [didVisible, dispatch, planId]);

  const handleLoadMoreButtonClick = useCallback(() => setItemsPage(prev => prev + 1), []);

  const editedApprovedHistoryChanges: IPlanHistoryItem[] = useMemo(() => {
    return approvedHistoryChanges
      .reduce((editedItems: IPlanHistoryItem[], item) => {
        const itemStatus: ErknmPlanStatusesEnum =
          mapErknmPlanStatusByStatusTitle[item.title as ErknmPlanStatusTitlesEnum];

        if (itemStatus === ErknmPlanStatusesEnum.CONSIDERED) {
          const prosecutorItem = {
            ...item,
            name: `${fullNameWhoConsideredPlan} - ${positionWhoConsideredPlan}`,
          };

          if (isSupervisor) {
            editedItems.push(prosecutorItem);

            return editedItems;
          }
          if (isProsecutorOrSuperProsecutor) {
            editedItems.push(prosecutorItem);
            editedItems.push(item);

            return editedItems;
          }
        }
        editedItems.push(item);

        return editedItems;
      }, [])
      .sort((a, b) => {
        return b.date.localeCompare(a.date);
      });
  }, [
    approvedHistoryChanges,
    isSupervisor,
    isProsecutorOrSuperProsecutor,
    fullNameWhoConsideredPlan,
    positionWhoConsideredPlan,
  ]);

  const isLoadMoreButtonVisible = editedApprovedHistoryChanges.length > lastVisibleItemsNumber;

  if (!planId) {
    return null;
  }

  return (
    <Section title="История согласования плана" id={KNM_PLAN_HISTORY}>
      <div ref={blockRef} />
      {isLoading && <Spinner className={styles.Loader} />}

      {editedApprovedHistoryChanges.length > 0 ? (
        <Row>
          {editedApprovedHistoryChanges.map((knmPlanHistoryItem, index) => {
            if (index > lastVisibleItemsNumber - 1) {
              return null;
            }

            return (
              <KnmCollapse
                key={uuidv4()}
                title={`Обновлено ${knmPlanHistoryItem.name} ${knmPlanHistoryItem.date} (МСК)`}
              >
                {knmPlanHistoryItem.oldValue
                  ? `Cтатус плана изменен с "${knmPlanHistoryItem.oldValue}" на "${knmPlanHistoryItem.title}"`
                  : `Cтатус плана изменен на "${knmPlanHistoryItem.title}"`}
              </KnmCollapse>
            );
          })}
        </Row>
      ) : (
        <Row>Сведения о согласовании плана отсутствуют</Row>
      )}

      {isLoadMoreButtonVisible && (
        <Button type="button" text="Показать еще" color="load" onClick={handleLoadMoreButtonClick} />
      )}
    </Section>
  );
};

const mapStateToProps = (state: IRootState) => ({
  planId: getKnmDataPlanId(state),
  isLoading: getPlanIsLoadingHistory(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  fullNameWhoConsideredPlan: getPlanListPlan(state)?.knmPlanData?.fullNameWhoConsideredPlan,
  positionWhoConsideredPlan: getPlanListPlan(state)?.knmPlanData?.positionWhoConsideredPlan,
  approvedHistoryChanges: getKnmPlanApprovedHistoryChanges(state),
});

export const ErknmPlanHistory = connect(mapStateToProps)(ErknmPlanHistoryWithoutStore);
